import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { useContext } from "react";
import UserContext from "../context/UserContext.js";
import HomeIcon from "../assets/icons/home.png";
import FriendsIcon from "../assets/icons/friend1.png";
import SearchIcon from "../assets/icons/search.png";
import SplitExpenseIcon from "../assets/icons/group.png";
import SettingIcon from "../assets/icons/gear1.png";
import { Preferences } from "@capacitor/preferences";

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    user,
    setUser,
    accessToken,
    refreshToken,
    setAccessToken,
    setRefreshToken,
    incomingRequests,
    setIncomingRequests,
  } = useContext(UserContext);

  const navRef = useRef();
  const startX = useRef(0);
  const startY = useRef(0);
  const closeX = useRef(0);
  const closeY = useRef(0);

  useEffect(() => {
    async function param() {
      if (user) {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/v1/friendRequests/receivedAll`,
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                accessToken,
                refreshToken,
              }),
            }
          );
          const data = await res.json();
          setIncomingRequests(data.data.senders);
        } catch (e) {
          console.error("Error fetching requests:", e);
        }
      }
    }
    param();
  }, [user, accessToken, refreshToken]);

  useEffect(() => {
    const handleTouchStart = (e) => {
      // Ignore gestures with multiple fingers
      if (e.touches.length > 1) {
        return;
      }
      startX.current = e.touches[0].clientX;
      startY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      // Ignore gestures with multiple fingers
      if (e.changedTouches.length > 1) {
        return;
      }
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const distanceX = endX - startX.current;
      const distanceY = Math.abs(endY - startY.current);

      // Detect swipe gesture for opening the menu
      if (distanceY <= 25 && distanceX > 40) {
        setMenuOpen(true);
      }
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  useEffect(() => {
    const handleTouchStart = (e) => {
      // Ignore gestures with multiple fingers
      if (e.touches.length > 1) {
        return;
      }
      closeX.current = e.touches[0].clientX;
      closeY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      // Ignore gestures with multiple fingers
      if (e.changedTouches.length > 1) {
        return;
      }
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const distanceX = closeX.current - endX;
      const distanceY = Math.abs(closeY.current - endY);

      // Detect swipe gesture for closing the menu
      if (distanceY <= 25 && distanceX > 40) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  const logout = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/users/logout`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            accessToken,
            refreshToken,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Logout failed");
      }

      await Preferences.remove({ key: "accessToken" });
      await Preferences.remove({ key: "refreshToken" });

      setAccessToken(null);
      setRefreshToken(null);

      setUser(null);
      window.location.reload();
    } catch (e) {
      console.error("Logout failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (menuOpen && navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [menuOpen]);

  return (
    <div className="flex">
      {/* Hamburger Menu Button */}
      <button
        className={`md:hidden text-white text-2xl p-2 pt-3 pl-2 fixed top-3 left-2 z-50 rounded-full shadow-[0_6px_20px_rgba(0,0,0,0.35)] ${
          menuOpen ? "hidden" : ""
        }`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle navigation menu"
      >
        <span>☰</span> {/* Replace with your desired hamburger menu icon */}
      </button>

      <div
        className={`md:hidden fixed top-8 left-6 transform -translate-x-1/2 -translate-y-1/2 z-50 p-4 flex justify-center items-center cursor-pointer ${
          menuOpen || incomingRequests.length < 1 ? "hidden" : ""
        }`}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <span className="flex items-center justify-center bg-green-600 text-white text-sm font-bold w-5 h-5 rounded-full shadow-lg">
          {incomingRequests.length}
        </span>
      </div>

      {/* Navbar */}
      <nav
        ref={navRef}
        className={`p-4 fixed left-0 top-0 max-w-[90%] w-80 h-full z-40 flex flex-col justify-between transition-transform duration-500 ease-in-out border-r border-white/5 backdrop-blur-0 shadow-[0_20px_60px_rgba(0,0,0,0.6)] ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
        style={{
          background:
            "radial-gradient(900px 400px at 100% 0%, rgba(59,130,246,0.06), rgba(0,0,0,0) 55%), radial-gradient(700px 350px at 0% 100%, rgba(34,197,94,0.06), rgba(0,0,0,0) 55%), linear-gradient(180deg, rgba(10,10,11,0.92) 0%, rgba(5,5,5,0.92) 100%)",
        }}
      >
        {/* Logo */}
        <div className="flex items-center space-x-1 py-6">
          <img src="/logo.png" className="w-14 h-14" alt="CashTrack Logo" />
          <Link
            to="/"
            className="henny-penny-regular text-4xl px-1 font-extrabold bg-clip-text text-transparent bg-gradient-to-br from-green-400 to-blue-500 animate-text transform transition-transform duration-300"
            aria-label="Navigate to CashTrack homepage"
          >
            Hisaab <span className="text-white">Kitab</span>
          </Link>
        </div>

        {/* Sidebar Navigation Links */}
        <div className="flex flex-col items-start space-y-1 md:space-y-2 p-4 w-full">
          {[
            { to: "/", label: "Home", icon: HomeIcon },
            { to: "/friends", label: "Friends", icon: FriendsIcon },
            { to: "/search", label: "Search", icon: SearchIcon },
            // {
            //   to: "/add-one",
            //   label: "Add Transaction",
            //   icon: AddTransactionIcon,
            // },
            {
              to: "/split-expense",
              label: "Split Expense",
              icon: SplitExpenseIcon,
            },
          ].map(({ to, label, icon }, index) => (
            <NavLink
              key={index}
              to={to}
              className={({ isActive }) =>
                `flex items-center justify-between w-full px-4 py-2 rounded-lg transition duration-300 ${
                  isActive
                    ? "bg-white/10 text-white border border-white/10"
                    : "text-gray-300 hover:bg-white/5"
                }`
              }
              onClick={() => setMenuOpen(false)}
            >
              <div className="flex items-center space-x-3">
                <img
                  src={icon}
                  alt={`${label} Icon`}
                  className="w-8 h-8 md:w-6 md:h-6"
                />
                <span className="font-medium text-sm uppercase tracking-wide">
                  {label}
                </span>
              </div>
            </NavLink>
          ))}

          {user && (
            <NavLink
              to="/incoming-requests"
              className={({ isActive }) =>
                `flex items-center justify-between w-full px-4 py-2 rounded-lg transition duration-300 ${
                  isActive
                    ? "bg-white/10 text-white border border-white/10"
                    : "text-gray-300 hover:bg-white/5"
                }`
              }
              onClick={() => setMenuOpen(false)}
            >
              <div className="flex items-center space-x-3">
                <span className="text-lg text-green-500">📩</span>
                <span className="font-medium text-sm uppercase tracking-wide">
                  Incoming Requests
                </span>
              </div>
              {incomingRequests.length > 0 && (
                <span className="flex items-center justify-center bg-green-500 text-white text-xs font-bold w-6 h-6 rounded-full shadow-lg animate-pulse">
                  {incomingRequests.length}
                </span>
              )}
            </NavLink>
          )}
        </div>

        {/* User Information */}
        <div className="md:p-4 p-1 text-white">
          {user ? (
            <div className="w-full flex flex-col space-y-2 md:space-y-3">
              {/* User Profile */}
              <Link
                to={`/users/${user.username}`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center space-x-3"
              >
                {/* Profile Picture */}
                <div className="w-12 h-12 rounded-full overflow-hidden border border-white/10">
                  <img
                    src={
                      user.profilePicture
                        ? `${user.profilePicture}`
                        : "/user2.png"
                    }
                    alt="User Profile"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* User Info */}
                <div className="flex-1 overflow-hidden">
                  <p className="font-medium text-sm text-white truncate">
                    {user.username || "User"}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {user.email || "example@email.com"}
                  </p>
                </div>
              </Link>

              {/* Settings Option */}
              <NavLink
                to="/settings"
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center justify-between w-full px-4 py-2 rounded-lg transition duration-300 ${
                    isActive
                      ? "bg-white/10 text-white border border-white/10"
                      : "text-gray-300 hover:bg-white/5"
                  }`
                }
              >
                <div className="flex items-center space-x-3">
                  <img
                    src={SettingIcon}
                    className="w-6 h-6"
                    alt="Settings Icon"
                  />
                  <span className="font-medium text-sm uppercase tracking-wide">
                    Settings
                  </span>
                </div>
              </NavLink>

              {/* Logout Button */}
              <button
                onClick={logout}
                className={`w-full flex items-center justify-center px-4 py-2 md:py-3 rounded-lg bg-gradient-to-r from-red-500 to-red-600 shadow-lg hover:from-red-600 hover:to-red-700 text-white font-bold tracking-wide transition-transform duration-300 ${
                  loading ? "opacity-75 cursor-not-allowed" : "hover:scale-105"
                }`}
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-5 w-5 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      ></path>
                    </svg>
                  </span>
                ) : (
                  "Logout"
                )}
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              className="w-full flex items-center justify-center px-4 py-3 rounded-lg text-white font-bold tracking-wide hover:scale-105 transform transition-transform duration-300 border border-white/10 backdrop-blur-md"
              style={{
                background:
                  "linear-gradient(180deg, rgba(2,6,23,0.75) 0%, rgba(3,7,18,0.75) 100%)",
                boxShadow: "0 5px 20px rgba(59,130,246,0.35)",
              }}
            >
              Login
            </Link>
          )}
        </div>
      </nav>
    </div>
  );
}

export default Navbar;
